import { Component } from '@theme/component';

class GyRotatingHero extends Component {
  /** @type {number | undefined} */
  #intervalId;

  /** @type {number | undefined} */
  #transitionId;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MediaQueryList | undefined} */
  #reducedMotionQuery;

  /** @type {HTMLElement | null} */
  #output = null;

  /** @type {string[]} */
  #words = [];

  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const { signal } = this.#abortController;
    document.addEventListener('visibilitychange', this.#handlePlaybackPreference, { signal });
    this.#reducedMotionQuery.addEventListener('change', this.#handlePlaybackPreference, { signal });

    this.#configure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#stop();
  }

  /** Reconfigure after Horizon morphs a section in place. */
  updatedCallback() {
    super.updatedCallback();
    this.#configure();
  }

  #configure() {
    this.#stop();
    this.#output = this.querySelector('[data-rotating-word]');
    this.#words = Array.from(this.querySelectorAll('[data-rotating-word-value]'), (element) =>
      element.textContent?.trim()
    ).filter((word) => Boolean(word));
    this.#index = 0;

    if (!this.#output || this.#words.length === 0) return;

    this.#output.textContent = this.#words[0];
    this.#output.style.opacity = '1';
    this.#start();
  }

  #start() {
    if (
      !this.isConnected ||
      this.#intervalId !== undefined ||
      !this.#output ||
      this.#words.length < 2 ||
      document.hidden ||
      this.#reducedMotionQuery?.matches
    ) {
      return;
    }

    const interval = Number.parseInt(this.dataset.rotationInterval ?? '', 10) || 2400;
    this.#intervalId = window.setInterval(this.#rotate, interval);
  }

  #stop() {
    if (this.#intervalId !== undefined) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }

    if (this.#transitionId !== undefined) {
      window.clearTimeout(this.#transitionId);
      this.#transitionId = undefined;
    }

    if (this.#output) this.#output.style.opacity = '1';
  }

  #rotate = () => {
    if (!this.#output) return;

    const nextIndex = (this.#index + 1) % this.#words.length;
    const duration = Number.parseInt(this.dataset.transitionDuration ?? '', 10) || 400;
    this.#output.style.opacity = '0';

    this.#transitionId = window.setTimeout(() => {
      if (!this.#output || !this.isConnected) return;

      this.#index = nextIndex;
      this.#output.textContent = this.#words[this.#index];
      this.#output.style.opacity = '1';
      this.#transitionId = undefined;
    }, duration);
  };

  #handlePlaybackPreference = () => {
    this.#stop();
    this.#start();
  };
}

if (!customElements.get('gy-rotating-hero')) {
  customElements.define('gy-rotating-hero', GyRotatingHero);
}
